<template>
    <div
        ref="rootRef"
        class="form-group-select"
        :class="{
            open: isOpen,
            disabled: disabled,
            'drop-up': dropdownDirection === FormSelectFixedDirection.Up,
            'drop-down': dropdownDirection === FormSelectFixedDirection.Down,
        }"
        @click="toggleDropdown"
    >
        <!-- 如果 autocomplete 開啟，用 input -->
        <input
            v-if="autocomplete"
            ref="inputRef"
            v-model="searchText"
            type="text"
            class="selected-text"
            :class="{ 'selected-strong': resolvedSelectedValue !== '' }"
            :placeholder="placeholder"
            :disabled="disabled"
            @input="onInput"
        />

        <!-- 否則用 span -->
        <span
            v-else
            class="selected-text"
            :class="{ 'selected-strong': resolvedSelectedValue !== '' }"
        >
            {{ selectedLabel }}
        </span>

        <!-- 下拉選單 -->
        <div v-show="isOpen" class="dropdown-options">
            <ul class="dropdown-scroll-container" :style="dropdownStyle">
                <li
                    v-for="option in displayOptions"
                    :key="String(option.value)"
                    :class="{
                        selected: option.value === resolvedSelectedValue,
                    }"
                    @click.stop="selectOption(option)"
                >
                    <template
                        v-for="(
                            segment, segmentIndex
                        ) in getOptionLabelSegments(option.label)"
                    >
                        <span
                            v-if="segment.isMatchedKeyword"
                            :key="`match-${String(option.value)}-${segmentIndex}`"
                            class="matched-keyword-text"
                        >
                            {{ segment.text }}
                        </span>
                        <span
                            v-else
                            :key="`plain-${String(option.value)}-${segmentIndex}`"
                        >
                            {{ segment.text }}
                        </span>
                    </template>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {
        computed,
        CSSProperties,
        nextTick,
        onBeforeUnmount,
        onMounted,
        ref,
        watch,
    } from 'vue';
    import { useAppLoadingStore } from '@/stores/loading.store';
    import {
        FormSelectFixedDirection,
        FormSelectOption,
    } from '@/types/components/base-components.types';

    interface LabelSegment {
        text: string;
        isMatchedKeyword: boolean;
    }

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            value?: string | number | null;
            selectedValue?: string | number | null;
            options: FormSelectOption[];
            rows?: number;
            autocomplete?: boolean;
            highlight?: boolean;
            placeholder?: string;
            fixedDirection?: FormSelectFixedDirection;
            disabled?: boolean;
        }>(),
        {
            value: null,
            selectedValue: '',
            rows: 0,
            autocomplete: false,
            highlight: false,
            placeholder: '',
            fixedDirection: FormSelectFixedDirection.None,
            disabled: false,
        }
    );

    // ----- emits -----
    const emits = defineEmits<{
        (event: 'input', value: string | number): void;
        (event: 'change', value: string | number): void;
        (event: 'search', keyword: string): void;
    }>();

    // ----- state -----
    const loadingStore = useAppLoadingStore();
    const rootRef = ref<HTMLElement | null>(null);
    const inputRef = ref<HTMLInputElement | null>(null);
    const inputActive = ref(false);
    const debounceTimer = ref<number | null>(null);
    const isOpen = ref(false);
    const searchText = ref('');
    const dropdownDirection = ref<FormSelectFixedDirection>(
        FormSelectFixedDirection.None
    );

    // ----- computed -----
    const resolvedSelectedValue = computed<string | number | ''>(() => {
        if (props.value !== null) {
            return props.value;
        }

        return props.selectedValue === null ? '' : props.selectedValue;
    });

    // 取得已選值標籤
    const selectedLabel = computed<string>(() => {
        const found = props.options.find(
            (opt) => opt.value === resolvedSelectedValue.value
        );
        return found ? found.label : '';
    });

    // autocomplete 模式下依搜尋文字即時過濾選項
    const displayOptions = computed<FormSelectOption[]>(() => {
        if (!props.autocomplete || searchText.value === '') {
            return props.options;
        }
        const kw = searchText.value.toLowerCase();
        return props.options.filter((opt) =>
            opt.label.toLowerCase().includes(kw)
        );
    });

    // 下拉清單高度限制
    const dropdownStyle = computed<CSSProperties>(() => {
        if (props.rows > 0) {
            const optionHeight = 32 + 8;
            return {
                paddingRight: '8px',
                maxHeight: `${props.rows * optionHeight}px`,
                overflowY: 'auto',
            };
        }

        return {};
    });

    // ----- methods -----
    // 調整選單方向 (固定方向優先)
    const adjustDropdownPosition = (): void => {
        if (!isOpen.value) return;

        nextTick(() => {
            requestAnimationFrame(() => {
                const root = rootRef.value;
                if (!root) return;

                if (props.fixedDirection) {
                    dropdownDirection.value = props.fixedDirection;
                    return;
                }

                const rootRect = root.getBoundingClientRect();
                const dropdown = root.querySelector(
                    '.dropdown-options'
                ) as HTMLElement | null;
                if (!dropdown) return;

                const viewportHeight = window.innerHeight;
                dropdownDirection.value =
                    rootRect.bottom + dropdown.offsetHeight > viewportHeight
                        ? FormSelectFixedDirection.Up
                        : FormSelectFixedDirection.Down;
            });
        });
    };

    // 將目前 selectedValue 對應為搜尋輸入字串；空值直接清空以顯示 placeholder
    const getSearchText = (): void => {
        const selectedVal = resolvedSelectedValue.value;
        if (selectedVal === '') {
            searchText.value = '';
            return;
        }
        const found = props.options.find((opt) => opt.value === selectedVal);
        searchText.value = found ? found.label : '';
    };

    // 依搜尋關鍵字切分標籤文字，命中片段用於視覺強調
    const getOptionLabelSegments = (label: string): LabelSegment[] => {
        const keyword = searchText.value.trim();

        if (!props.autocomplete || !props.highlight || !keyword) {
            return [
                {
                    text: label,
                    isMatchedKeyword: false,
                },
            ];
        }

        const normalizedLabel = label.toLowerCase();
        const normalizedKeyword = keyword.toLowerCase();
        const segments: LabelSegment[] = [];
        let startIndex = 0;

        while (startIndex < label.length) {
            const matchIndex = normalizedLabel.indexOf(
                normalizedKeyword,
                startIndex
            );

            if (matchIndex === -1) {
                segments.push({
                    text: label.slice(startIndex),
                    isMatchedKeyword: false,
                });
                break;
            }

            if (matchIndex > startIndex) {
                segments.push({
                    text: label.slice(startIndex, matchIndex),
                    isMatchedKeyword: false,
                });
            }

            const matchedEndIndex = matchIndex + keyword.length;
            segments.push({
                text: label.slice(matchIndex, matchedEndIndex),
                isMatchedKeyword: true,
            });

            startIndex = matchedEndIndex;
        }

        return segments.length > 0
            ? segments
            : [
                  {
                      text: label,
                      isMatchedKeyword: false,
                  },
              ];
    };

    // 開關下拉選單
    const toggleDropdown = (): void => {
        if (props.disabled) return;
        if (props.autocomplete && isOpen.value) return;

        isOpen.value = !isOpen.value;
        adjustDropdownPosition();
    };

    // autocomplete 搜尋輸入
    const onInput = (): void => {
        if (!props.autocomplete) return;

        if (!inputActive.value) {
            loadingStore.start();
        }
        inputActive.value = true;

        // 停止輸入後 1000ms 觸發 search
        if (debounceTimer.value) {
            window.clearTimeout(debounceTimer.value);
        }

        debounceTimer.value = window.setTimeout(() => {
            emits('search', searchText.value);
            inputActive.value = false;
            loadingStore.end();
            debounceTimer.value = null;
        }, 1000);
    };

    // 選擇選項並同步輸出 input/change
    const selectOption = (option: FormSelectOption): void => {
        if (props.disabled) return;

        const oldValue = resolvedSelectedValue.value;
        const newValue = option.value;

        if (newValue !== oldValue) {
            emits('input', newValue);
            emits('change', newValue);
        }

        if (props.autocomplete) {
            searchText.value = option.label;
        }

        isOpen.value = false;
    };

    // 點擊外部時關閉選單
    const handleClickOutside = (event: Event): void => {
        if (props.disabled && !isOpen.value) return;

        const root = rootRef.value;
        const target = event.target as Node | null;
        if (root && target && !root.contains(target)) {
            isOpen.value = false;
        }
    };

    // ----- watch -----
    watch(isOpen, (newValue) => {
        if (props.autocomplete && inputRef.value) {
            if (newValue) {
                inputRef.value.focus();
                searchText.value = '';
            } else {
                inputRef.value.blur();
                getSearchText();
            }
        }
    });

    // 外部直接修改 selectedValue 時，同步更新 searchText
    watch(resolvedSelectedValue, () => {
        if (props.autocomplete && !isOpen.value) {
            getSearchText();
        }
    });

    // ----- lifecycle -----
    onMounted(() => {
        document.addEventListener('click', handleClickOutside);

        if (!props.fixedDirection) {
            window.addEventListener('resize', adjustDropdownPosition);
            document.addEventListener('scroll', adjustDropdownPosition, true);
        }

        if (props.autocomplete) {
            getSearchText();
        }
    });

    onBeforeUnmount(() => {
        document.removeEventListener('click', handleClickOutside);

        if (!props.fixedDirection) {
            window.removeEventListener('resize', adjustDropdownPosition);
            document.removeEventListener(
                'scroll',
                adjustDropdownPosition,
                true
            );
        }

        if (debounceTimer.value) {
            window.clearTimeout(debounceTimer.value);
            debounceTimer.value = null;
        }
    });
</script>

<style lang="scss" scoped>
    .matched-keyword-text {
        font-weight: 700;
        text-decoration: underline;
    }
</style>
